import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, MessageCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { createContact } from '../api';

const WHATSAPP_NUMBER = '918279765078';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createContact(formData);
    } catch (err) {
      // proceed to WhatsApp regardless
    }
    const message = encodeURIComponent(
      `🌿 *New Contact Query - Aatreya Herbals*\n\n` +
      `👤 *Name:* ${formData.name}\n` +
      `📧 *Email:* ${formData.email}\n` +
      `📱 *Phone:* ${formData.phone}\n\n` +
      `📝 *Message:* ${formData.message}`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
    toast({
      title: 'Message Sent! 🎉',
      description: 'Your query has been sent via WhatsApp. We will respond shortly.',
    });
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Us',
      details: ['Aatreya Herbals', 'Saharanpur, Uttar Pradesh']
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: ['+91 8279765078'],
      link: 'tel:+918279765078'
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: ['aatreyaherbals@gmail.com'],
      link: 'mailto:aatreyaherbals@gmail.com'
    },
    {
      icon: Clock,
      title: 'Working Hours',
      details: ['Mon - Sat: 10 AM - 7 PM', 'Sunday: Closed']
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="contact-page">
      {/* Hero */}
      <section className="bg-primary/5 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-4"
          >
            <h1 className="text-5xl sm:text-6xl font-bold font-serif">Get In Touch</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Have questions about our health kits or want to book a consultation? We&apos;re here to help.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        {/* Contact Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {contactInfo.map((info, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="bg-card rounded-2xl border border-border p-6 text-center shadow-lg hover:shadow-xl transition-all"
            >
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <info.icon className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-serif font-semibold text-lg mb-2">{info.title}</h3>
              {info.details.map((detail, i) =>
                info.link ? (
                  <a
                    key={i}
                    href={info.link}
                    className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {detail}
                  </a>
                ) : (
                  <p key={i} className="text-sm text-muted-foreground">{detail}</p>
                )
              )}
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-card rounded-2xl border border-border p-8 shadow-xl"
          >
            <h2 className="text-3xl font-serif font-bold mb-2">Send us a Message</h2>
            <p className="text-muted-foreground mb-6">We&apos;ll get back to you via WhatsApp</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  required
                  placeholder="Enter your name"
                  className="mt-2"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  placeholder="your@email.com"
                  className="mt-2"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  required
                  placeholder="+91 XXXXX XXXXX"
                  className="mt-2"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  required
                  placeholder="How can we help you?"
                  className="mt-2 min-h-[120px]"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  data-testid="contact-message-input"
                />
              </div>
              <Button
                type="submit"
                size="lg"
                className="w-full bg-green-500 hover:bg-green-600 text-white"
                data-testid="contact-submit-button"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Send via WhatsApp
              </Button>
            </form>
          </motion.div>

          {/* Map & Quick Contact */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="bg-card rounded-2xl border border-border overflow-hidden shadow-xl h-80">
              <iframe
                title="Aatreya Herbals Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13836.9!2d77.5460!3d29.9640!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zU2FoYXJhbnB1cg!5e0!3m2!1sen!2sin!4v1600000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
              />
            </div>

            <div className="bg-primary rounded-2xl p-8 text-center">
              <MessageCircle className="w-12 h-12 text-primary-foreground mx-auto mb-4" />
              <h3 className="text-2xl font-serif font-bold text-primary-foreground mb-2">
                Chat with us on WhatsApp
              </h3>
              <p className="text-primary-foreground/90 mb-6">
                Get instant responses to your queries
              </p>
              <Button
                onClick={() =>
                  window.open(
                    `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hello Aatreya Herbals! I have a query.')}`,
                    '_blank'
                  )
                }
                size="lg"
                variant="secondary"
                data-testid="whatsapp-chat-button"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Start Chat
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
